import type { UniqueIdentifier } from "@dnd-kit/core";
import type { ComponentType, SVGAttributes } from "react";

export type TreeIconComponent = ComponentType<SVGAttributes<SVGSVGElement>>;

export enum TreeItemType {
  View = "view",
  Folder = "folder",
}

export interface AbstractTreeItem {
  id: UniqueIdentifier;
  name: string;
  friendlyName: string;
  type: TreeItemType;
  notificationCount?: number;
  /** Optional leading icon for the row. Folders fall back to Folder/FolderOpen when not set. */
  Icon?: TreeIconComponent;
  /** Optional collapsed-state icon for folder rows. Defaults to `Icon` when not set. */
  IconCollapsed?: TreeIconComponent;
  /**
   * Only applies in state-driven mode (SortableTree given `onSelect`). When false, the
   * item cannot be selected — folders remain click-to-toggle-expand.
   */
  selectable?: boolean;
  /**
   * Folder-only. When true, this folder's direct children can only reorder among
   * themselves via drag — they cannot be dropped into a different parent, and
   * non-children cannot be dropped into this folder.
   */
  lockChildren?: boolean;
  /** When true, the row renders at reduced opacity to signal an inactive/muted state. */
  dimmed?: boolean;
  /** When false, this item cannot be dragged. Defaults to true. */
  draggable?: boolean;
}

export interface TreeViewItem extends AbstractTreeItem {
  type: TreeItemType.View;
  /** URL for link-based navigation. Omit when using state-driven selection via `onSelect`. */
  actionHref?: string;
}

export interface TreeFolderItem extends AbstractTreeItem {
  type: TreeItemType.Folder;
  children: TreeItem[];
}

export type TreeItem = TreeViewItem | TreeFolderItem;

export type FlattenedItem = TreeItem & {
  parentId: UniqueIdentifier | null;
  depth: number;
  index: number;
};
